if (!customElements.get('sticky-button')) {
  class StickyButton extends HTMLElement {
    constructor() {
      super();
      
      // Configuration
      this.scrollThreshold = 200; // px to scroll before showing sticky
      this.animationDuration = 300; // ms for slide animation
      this.infoWrapper = document.querySelector('.product__info-wrapper');
      
      // State
      this.isSticky = false;
      this.isVisible = false;
      this.originalPosition = null;
      
      // Bind methods
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);

      // Buttons
      this.submitButton = this.querySelector('button[name="add"]');
      this.stickyTarget = this.querySelector('.sticky-target');
      this.stickyAtcBtn = this.querySelector('.sticky-atc-btn');

      // Proxy variant picker (radios on the PDP)
      this.variantRadios =
        (this.closest('section') && this.closest('section').querySelector('variant-radios')) ||
        document.querySelector('variant-radios');
      this.stickySelects = Array.from(this.querySelectorAll('.sticky-atc-bar__select-input'));

      // Loading overlay (shown while a variant change is in flight)
      this.loadingOverlay = this.querySelector('.sticky-atc-bar__loading');
      this.loadingMinMs = 250;
      this.loadingDebounceMs = 180;
      this.loadingShownAt = 0;
      this.loadingDebounceTimer = null;
      this.loadingHideTimer = null;

      // Initialize
      this.init();

      // Bar ATC button proxies the real submit button
      if (this.stickyAtcBtn) {
        this.stickyAtcBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.stickyAtcBtn.hasAttribute('disabled')) return;
          this.submitButton.click();
        });
      }

      this.setupVariantSync();
    }

    setupVariantSync() {
      // Forward: bar dropdown -> PDP radios
      if (this.variantRadios) {
        this.stickySelects.forEach((select) => {
          select.addEventListener('change', () => {
            const name = select.dataset.stickyOptionName;
            const radios = this.variantRadios.querySelectorAll(`input[name="${CSS.escape(name)}"]`);
            radios.forEach((radio) => {
              radio.checked = radio.value === select.value;
            });
            const checked = this.variantRadios.querySelector(`input[name="${CSS.escape(name)}"]:checked`);
            if (checked) {
              checked.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });

        // Reverse: PDP radios -> bar dropdowns. Fires for both bar-driven and
        // direct radio changes, so use it to kick off the loading overlay.
        this.variantRadios.addEventListener('change', () => {
          this.stickySelects.forEach((select) => {
            const name = select.dataset.stickyOptionName;
            const checked = this.variantRadios.querySelector(`input[name="${CSS.escape(name)}"]:checked`);
            if (checked && select.value !== checked.value) {
              select.value = checked.value;
            }
          });
          this.startLoading();
        });
      }

      // Mirror the real submit button's state onto the bar ATC button.
      // renderProductInfo() updates the real button asynchronously, so observe it.
      if (this.submitButton && this.stickyAtcBtn) {
        this.syncButtonState();
        this.buttonObserver = new MutationObserver(() => {
          this.syncButtonState();
          // A button mutation means the variant update is still settling —
          // extend the loading debounce so the overlay lifts only when done.
          if (this.loadingOverlay && this.loadingOverlay.classList.contains('is-active')) {
            this.scheduleHideLoading();
          }
        });
        this.buttonObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'data-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    }

    startLoading() {
      if (!this.loadingOverlay) return;
      if (!this.loadingOverlay.classList.contains('is-active')) {
        this.loadingOverlay.classList.add('is-active');
        this.loadingShownAt = Date.now();
      }
      this.scheduleHideLoading();
    }

    scheduleHideLoading() {
      // Debounce: hide once mutations have stopped for loadingDebounceMs,
      // but keep the overlay up for at least loadingMinMs to avoid a flash.
      clearTimeout(this.loadingDebounceTimer);
      clearTimeout(this.loadingHideTimer);
      this.loadingDebounceTimer = setTimeout(() => {
        const elapsed = Date.now() - this.loadingShownAt;
        const wait = Math.max(0, this.loadingMinMs - elapsed);
        this.loadingHideTimer = setTimeout(() => {
          if (this.loadingOverlay) this.loadingOverlay.classList.remove('is-active');
        }, wait);
      }, this.loadingDebounceMs);
    }

    syncButtonState() {
      const disabled = this.submitButton.hasAttribute('disabled');
      const barText = this.stickyAtcBtn.querySelector('.js-text');
      const barIcon = this.stickyAtcBtn.querySelector('.js-icon');

      // Bar label stays constant ("Add") in every state so the layout never
      // reflows when the variant / availability changes.
      if (barText) barText.textContent = this.stickyAtcBtn.dataset.defaultText;

      if (disabled) {
        this.stickyAtcBtn.setAttribute('disabled', 'disabled');
        this.stickyAtcBtn.setAttribute('data-disabled', 'true');
        if (barIcon) barIcon.classList.add('hidden');
      } else {
        this.stickyAtcBtn.removeAttribute('disabled');
        this.stickyAtcBtn.setAttribute('data-disabled', 'false');
        if (barIcon) barIcon.classList.remove('hidden');
      }
    }
    
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.setup();
        });
      } else {
        this.setup();
      }
    }
    
    setup() {
      // Store original position and styles
      this.storeOriginalState();
      
      // Add event listeners
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      
      // Initial check after a short delay to ensure proper positioning
      setTimeout(() => {
        this.handleScroll();
      }, 100);
    }
    
    storeOriginalState() {
      const rect = this.getBoundingClientRect();
      this.originalPosition = {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      };
    }
    
    handleScroll() {
      // Only enable sticky functionality on mobile (below 750px)
      if (window.innerWidth >= 750) {
        // Reset to normal state on desktop
        if (this.isSticky) {
          this.isSticky = false;
          this.updateStickyState();
        }
        return;
      }
      
      const scrollY = window.scrollY;
      const elementTop = this.originalPosition.top;
      const elementBottom = elementTop + this.originalPosition.height;
      const viewportHeight = window.innerHeight;

      let hasScrolledPastThreshold = scrollY >= this.scrollThreshold;
      
      // Check if we've scrolled past the threshold
      if (this.infoWrapper) {
        const infoWrapperHeight = window.innerHeight - (this.infoWrapper.offsetTop - scrollY);
        const twentyPercent = window.innerHeight * 0.2;

        hasScrolledPastThreshold = infoWrapperHeight >= twentyPercent;
      }

      
      // Check if the element is currently in view
      // Element is in view when: scrollY < elementBottom AND scrollY + viewportHeight > elementTop
      const isElementInView = scrollY < elementBottom && scrollY + viewportHeight > elementTop;
      
      // Determine sticky state:
      // - Initially not sticky (before threshold)
      // - After threshold: sticky when element is NOT in view
      let shouldBeSticky = false;
      
      if (hasScrolledPastThreshold) {
        shouldBeSticky = !isElementInView;
      }
      
      // Update sticky state if needed
      if (shouldBeSticky !== this.isSticky) {
        this.isSticky = shouldBeSticky;
        this.updateStickyState();
      }
      
      // Update visibility - show when element is not in view and we've scrolled past threshold
      if (shouldBeSticky !== this.isVisible) {
        this.isVisible = shouldBeSticky;
      }
    }
    
    updateStickyState() {
      if (this.isSticky) {
        this.makeSticky();
      } else {
        this.makeNormal();
      }
    }
    
    makeSticky() {
      // Add sticky class for additional styling
      this.classList.add('sticky-button--sticky');

      // Expose the bar height so the chat bubble can clear it
      if (this.stickyTarget) {
        document.documentElement.style.setProperty('--sticky-bar-h', `${this.stickyTarget.offsetHeight}px`);
      }

      this.chatButton = document.querySelector('inbox-online-store-chat');

      if (this.chatButton) {
        this.chatButton.classList.add('sticky');
      }
    }
    
    makeNormal() {
      // Remove sticky class
      this.classList.remove('sticky-button--sticky');
      this.chatButton = document.querySelector('inbox-online-store-chat');
      if (this.chatButton) {
        this.chatButton.classList.remove('sticky');
      }
    }
    
    handleResize() {
      // Recalculate original position on resize
      this.storeOriginalState();
      this.handleScroll();
    }
    
    disconnectedCallback() {
      // Clean up event listeners
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      if (this.buttonObserver) this.buttonObserver.disconnect();
      clearTimeout(this.loadingDebounceTimer);
      clearTimeout(this.loadingHideTimer);
    }
  }
  
  customElements.define('sticky-button', StickyButton);
} 